import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';

interface PPCSituationHistoryDashboardProps {
  product: PMT2Product | null;
}

interface PPCSituationEntry {
  id: string;
  date: Timestamp; // Firestore Timestamp
  Date: string; // Original Date string from sheet (MM/DD/YY or similar) - Keep for potential display if needed
  'Your Name': string;
  note: string;
  [key: string]: any;
}

/**
 * Overall PPC Situation History Dashboard Component
 *
 * Displays historical PPC situation entries for a selected product
 * Each entry shows Days Ago, Date, Reviewer, and Notes as bullet points
 */
const PPCSituationHistoryDashboard: React.FC<PPCSituationHistoryDashboardProps> = ({ product }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [situationEntries, setSituationEntries] = useState<PPCSituationEntry[]>([]);

  // Memoize the fetch function to avoid recreating it on every render
  const fetchPPCSituationHistory = useCallback(async (companyName: string, asin: string) => {
    // Reset state
    setSituationEntries([]);
    setError(null);
    setLoading(true);

    try {
      const entries = await pmt2Service.getPPCSituationHistory(companyName, asin);

      // Only update state if the product hasn't changed during the fetch
      if (product && product.id === asin && product.companyName === companyName) {
        setSituationEntries(entries);
      }
    } catch (err) {
      setError('Failed to load PPC situation history. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [product]);

  // Effect to fetch data when product changes
  useEffect(() => {
    // Reset state when product changes
    setSituationEntries([]);
    setError(null);

    if (product) {
      fetchPPCSituationHistory(product.companyName, product.id);
    } else {
        setLoading(false); // Ensure loading stops if product becomes null
    }
  }, [product, fetchPPCSituationHistory]);

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Overall PPC Situation History</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading PPC situation history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Overall PPC Situation History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  // Sort entries by date (newest first) using the reliable Firestore Timestamp
  const sortedEntries = [...situationEntries].sort((a, b) => {
    // Use Firestore Timestamp 'date' field directly for reliable sorting
    // Provide a default far-past date if timestamp is missing/invalid to avoid errors
    // and ensure entries without dates are sorted consistently (e.g., at the end).
    const timeA = a.date instanceof Timestamp ? a.date.toMillis() : 0;
    const timeB = b.date instanceof Timestamp ? b.date.toMillis() : 0;
    // Sort descending (newest first)
    return timeB - timeA;
  });


  // Calculate days ago from Firestore Timestamp
  const calculateDaysAgoFromTimestamp = (timestamp: Timestamp | undefined): number => {
    if (!timestamp || !(timestamp instanceof Timestamp)) return 0; // Return 0 if no valid timestamp

    try {
      const entryDate = timestamp.toDate();
      entryDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Calculate the difference in days
      const diffTime = today.getTime() - entryDate.getTime();
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      console.error("Error calculating days ago from timestamp:", error);
      return 0;
    }
  };

  // Format Firestore Timestamp to "Month Day, Year" format
  const formatDateFromTimestamp = (timestamp: Timestamp | undefined): string => {
    if (!timestamp || !(timestamp instanceof Timestamp)) return 'Unknown date';

    try {
      const date = timestamp.toDate();
      // Format the date as "Month Day, Year"
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return 'Invalid date';
    }
  };

  // Format notes as bullet points without the dashes
  const formatNotes = (note: string): string[] => {
    if (!note) return [];

    // Remove "^" and all text after it
    let cleanedNote = note;
    const caretIndex = note.indexOf('^');
    if (caretIndex !== -1) {
      cleanedNote = note.substring(0, caretIndex).trim();
    }

    // Check if the note starts with a dash or contains " - "
    const startsWithDash = cleanedNote.trim().startsWith('-');
    const containsDashSeparator = cleanedNote.includes(' - ');

    if (startsWithDash || containsDashSeparator) {
        // Split intelligently: handle leading dash and " - " separators
        // Remove leading dash first if present
        const noteToSplit = startsWithDash ? cleanedNote.trim().substring(1).trim() : cleanedNote.trim();
        // Split by newline potentially followed by " - " or just newline if that fails
        let parts = noteToSplit.split(/\n\s*-\s*|\n/);
        // If splitting by newline didn't work well (e.g., single line with " - "), try splitting by " - "
        if (parts.length <= 1 && noteToSplit.includes(' - ')) {
             parts = noteToSplit.split(/\s*-\s*/);
        }
        // Add back the first part if it started with a dash and wasn't captured
        if (startsWithDash && cleanedNote.split(/\s*-\s*/).length > parts.length) {
             const firstPart = cleanedNote.split(/\s*-\s*/)[0];
             if (firstPart) parts.unshift(firstPart.trim());
        }

        return parts.map(p => p.trim()).filter(item => item !== '');
    } else {
      // If no dashes, return the cleaned note as a single item
      return [cleanedNote];
    }
  };


  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">Overall PPC Situation History</h2>

      {sortedEntries.length === 0 && !loading ? ( // Show message only if not loading and empty
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No PPC situation history found for this ASIN.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedEntries.map((entry, index) => {
            // Use the Firestore Timestamp 'date' field
            const daysAgo = calculateDaysAgoFromTimestamp(entry.date);
            const notes = formatNotes(entry.note);
            const formattedDate = formatDateFromTimestamp(entry.date);

            return (
              <div key={`entry_${index}_${entry.id || Math.random().toString(36).substr(2, 9)}`} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-700">
                <div className="flex flex-wrap gap-4 items-center mb-2 text-sm">
                  <span className="font-medium">{daysAgo} days ago</span>
                  <span className="text-gray-500 dark:text-gray-400">{formattedDate}</span>
                  <span className="text-gray-500 dark:text-gray-400">{entry['Your Name'] || 'Unknown reviewer'}</span>
                </div>

                {notes.length > 0 ? (
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                    {notes.map((note, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">{note}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic mt-2 text-sm">No notes provided</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PPCSituationHistoryDashboard;